'use client'
import styled from 'styled-components'
import { Headline } from '@shipt/design-system-typography'
import { BlogPostDate } from '@/features/entrypoint/components/BlogPage/BlogPostDate'
import { screenSizes } from '@shipt/design-system-themes'
import { sanitizeHTML } from '@/features/shared/utils/sanitize'
import { BlogPostImage } from '@/features/entrypoint/components/BlogPage/BlogPostImage'
import { type BlogPostData } from '@/features/shared/serverUtils/blog'
import { theme } from '@/features/shared/constants/shiptTheme'

type Props = {
  postData: BlogPostData
}

export const BlogPost = ({ postData }: Props) => {
  const { title, content = '', seo } = postData

  return (
    <section>
      <BlogContentContainer postData={postData}>
        <BlogPostImage src={seo.image} />
        <PostTitle size="xl">{title}</PostTitle>
        <Content
          dangerouslySetInnerHTML={{
            __html: sanitizeHTML(content, { ADD_TAGS: ['iframe'] }),
          }}
        />
      </BlogContentContainer>
    </section>
  )
}

export const BlogContentContainer = ({
  postData,
  children,
}: Props & { children: React.ReactNode }) => {
  return (
    <Wrapper>
      <DateWrapper>
        <BlogPostDate date={postData.date} />
      </DateWrapper>
      <div>{children}</div>
    </Wrapper>
  )
}

const Wrapper = styled.article`
  max-width: 50rem;
  margin: 5.625rem auto;
  padding: 0 2rem;
  display: flex;
  flex-direction: column;
  white-space: pre-wrap;

  @media ${screenSizes.tablet} {
    flex-direction: row;
  }

  @media ${screenSizes.smDesktop} {
    padding: 0;
  }
`

const DateWrapper = styled.div`
  margin-bottom: 1rem;

  @media ${screenSizes.tablet} {
    margin-right: 2.75rem;
  }
`

export const PostTitle = styled(Headline)`
  margin-bottom: 1rem;
`

const Content = styled.div`
  font-size: 1.0625rem;
  line-height: 1.5rem;
  color: ${theme.gray800};

  a {
    color: ${theme.purple500};

    &:hover,
    &:focus {
      color: ${theme.purple700};
    }
  }

  iframe {
    border-width: 0;
  }

  video {
    width: 100%;
    height: auto;
  }

  img {
    max-inline-size: 100%;
    block-size: auto;
  }

  /* responsive youtube embeds */
  .embed-container {
    position: relative;
    padding-bottom: 56.25%;
    height: 0;
    overflow: hidden;
    max-width: 100%;

    iframe {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
    }
  }
`
